/**
 * Dialog for removing a user from the account
 * Handles orphan detection and optional user deletion
 */
import { useState } from "react";

import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { trpc } from "@/lib/trpc";

interface RemoveUserConfirmDialogProps {
	user: { id: string; name: string | null; email: string };
	onClose: () => void;
	onSuccess: () => void;
}

/**
 * Two-stage removal dialog:
 * 1. Confirm removal from account
 * 2. If user becomes orphaned, ask whether to delete the user record
 * @param root0 - Component props
 * @param root0.user - The user to remove
 * @param root0.onClose - Callback when dialog is closed
 * @param root0.onSuccess - Callback when user is successfully removed
 */
export function RemoveUserConfirmDialog({
	user,
	onClose,
	onSuccess,
}: RemoveUserConfirmDialogProps) {
	const [stage, setStage] = useState<"confirm" | "orphan">("confirm");
	const [orphanInfo, setOrphanInfo] = useState<{ userName: string | null; userEmail: string } | null>(null);

	// Remove user mutation
	const removeUserMutation = trpc.permissionGroups.removeUserFromAccount.useMutation({
		onSuccess: (data) => {
			// Check if response indicates user is orphaned
			const orphanData = data as { orphaned?: boolean; userName?: string | null; userEmail?: string };
			if (orphanData.orphaned) {
				// User will be orphaned, prompt for deletion
				setOrphanInfo({
					userName: orphanData.userName ?? null,
					userEmail: orphanData.userEmail ?? user.email,
				});
				setStage("orphan");
			}
			else {
				// Successfully removed
				onSuccess();
			}
		},
	});

	/**
	 * Initiates the user removal process
	 */
	const handleInitialRemove = () => {
		// First call without deleteOrphan to check if user will be orphaned
		removeUserMutation.mutate({ userId: user.id });
	};

	/**
	 * Handles the decision to delete or keep an orphaned user
	 * @param shouldDelete - Whether to delete the orphaned user record
	 */
	const handleDeleteOrphan = (shouldDelete: boolean) => {
		if (shouldDelete) {
			// Call again with deleteOrphan: true
			removeUserMutation.mutate({ userId: user.id, deleteOrphan: true });
		}
		else {
			// Keep orphaned user record
			onSuccess();
		}
	};

	return (
		<Dialog open onOpenChange={onClose}>
			<DialogContent className="sm:max-w-[450px]">
				{stage === "confirm" ? (
					<form onSubmit={(e) => { e.preventDefault(); handleInitialRemove(); }}>
						<DialogHeader>
							<DialogTitle>Remove User from Account</DialogTitle>
							<DialogDescription>
								This will remove <strong>{user.name ?? user.email}</strong> from this account.
								They will lose access to all teams within this account.
							</DialogDescription>
						</DialogHeader>

						<div className="py-4">
							<div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800 dark:bg-amber-950 dark:border-amber-800 dark:text-amber-200">
								<strong>Warning:</strong> This action cannot be undone. The user will need
								to be re-invited to regain access.
							</div>

							{removeUserMutation.isError && (
								<div className="mt-4 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
									{removeUserMutation.error.message}
								</div>
							)}
						</div>

						<DialogFooter>
							<Button type="button" variant="outline" onClick={onClose}>
								Cancel
							</Button>
							<Button
								variant="destructive"
								onClick={handleInitialRemove}
								disabled={removeUserMutation.isPending}
							>
								{removeUserMutation.isPending ? "Removing..." : "Remove User"}
							</Button>
						</DialogFooter>
					</form>
				) : (
					<form onSubmit={(e) => { e.preventDefault(); handleDeleteOrphan(true); }}>
						<DialogHeader>
							<DialogTitle>Delete User Record?</DialogTitle>
							<DialogDescription>
								<strong>{orphanInfo?.userName ?? orphanInfo?.userEmail}</strong> no longer has
								access to any other accounts. Would you like to also delete their user record?
							</DialogDescription>
						</DialogHeader>

						<div className="py-4">
							<div className="rounded-md bg-muted p-3 text-sm">
								<p className="mb-2"><strong>Keep user:</strong> The user record remains but has no access to any accounts. They can be re-invited later.</p>
								<p><strong>Delete user:</strong> Permanently removes the user record from the system.</p>
							</div>
						</div>

						<DialogFooter>
							<Button
								type="button"
								variant="outline"
								onClick={() => { handleDeleteOrphan(false); }}
								disabled={removeUserMutation.isPending}
							>
								Keep User
							</Button>
							<Button
								variant="destructive"
								onClick={() => { handleDeleteOrphan(true); }}
								disabled={removeUserMutation.isPending}
							>
								{removeUserMutation.isPending ? "Deleting..." : "Delete User"}
							</Button>
						</DialogFooter>
					</form>
				)}
			</DialogContent>
		</Dialog>
	);
}
